import { Button, Tooltip } from '@mui/material';
import { Category, WatchlistItem } from '@thro-types/users';

import { format, isToday } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useWatchlistMutation, useWatchlistListQuery } from 'api/hooks/watchlist';
import { TypedGridColumns } from 'app/components';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

export const useWatchlistColumns = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { deletion } = useWatchlistMutation();
  const { refetch } = useWatchlistListQuery();
  const [loading, setLoading] = useState<boolean>(false);

  const handleDelete = useCallback(
    async (watchlistItemId: number) => {
      setLoading(true);
      await deletion
        .mutateAsync(watchlistItemId)
        .then(() => {
          dispatch(AlertActions.success(t(translations.pages.home.watchlist.deleteSuccess())));
        })
        .catch(err => {
          dispatch(AlertActions.error(t(translations.pages.home.watchlist.deleteError())));
        })
        .finally(() => {
          setLoading(false);
        });
      refetch();
    },
    [deletion, dispatch, refetch, t]
  );

  const formatDate = (date: Date) => {
    return format(new Date(date), 'dd.MM.yyyy');
  };

  const showDateField = useCallback(
    (category: string, date?: Date) => {
      if (!date) return <div>{t(translations.pages.watchlist.columns.noInfo())}</div>;
      switch (category) {
        case 'MODULE_SELECTION':
          return (
            <Tooltip
              title={
                isToday(new Date(date)) ? t(translations.pages.watchlist.columns.tooltip()) : ''
              }
            >
              <div style={{ color: isToday(new Date(date)) ? 'red' : 'inherit' }}>
                {`${t(translations.pages.watchlist.columns.startdate())}: ${formatDate(date)}`}
              </div>
            </Tooltip>
          );
        case 'ARTICLE':
          return `${t(translations.pages.watchlist.columns.releaseDate())}: ${formatDate(date)}`;
        case 'EVENT':
          return (
            <Tooltip
              title={
                isToday(new Date(date)) ? t(translations.pages.watchlist.columns.tooltip()) : ''
              }
            >
              <div style={{ color: isToday(new Date(date)) ? 'red' : 'inherit' }}>
                {`${t(translations.pages.watchlist.columns.eventDate())}: ${formatDate(date)}`}
              </div>
            </Tooltip>
          );
        case 'STUDENT_OFFER':
          return `${t(translations.pages.watchlist.columns.notedOn())}: ${formatDate(date)}`;
        case 'JOB_OFFER':
          return `${t(translations.pages.watchlist.columns.notedOn())}: ${formatDate(date)}`;
        case 'FUNDING':
          return `${t(translations.pages.watchlist.columns.deadline())}: ${formatDate(date)}`;
        case 'BULLETIN':
          return `${t(translations.pages.watchlist.columns.releaseDate())}: ${formatDate(date)}`;
        case 'COMPANY':
          return `${t(translations.pages.watchlist.columns.notedOn())}: ${formatDate(date)}`;
      }
    },
    [t]
  );
  return useMemo((): TypedGridColumns<WatchlistItem & { category: Category }> => {
    return [
      {
        field: 'title',
        flex: 2,
        headerName: t(translations.pages.watchlist.columns.title()),
        renderCell: params => (
          <a
            className="fundings__link"
            href={params.row.link}
            target="_blank"
            rel="noreferrer"
            style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
          >
            {params.row.title}
          </a>
        )
      },
      {
        field: 'description',
        flex: 3,
        headerName: t(translations.pages.watchlist.columns.description()),
        sortable: false,
        renderCell: params => {
          const regex = /(<([^>]+)>)/gi;
          const textWithoutHTMLTags = params.value.replace(regex, '');
          return (
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={textWithoutHTMLTags}
            >
              {textWithoutHTMLTags}
            </div>
          );
        }
      },
      {
        field: 'category',
        flex: 1,
        headerName: t(translations.pages.watchlist.columns.category()),
        renderCell: params => {
          if (!params.row.category) return;
          return (
            <a href={params.row.link} target="_blank" rel="noreferrer">
              <div
                dangerouslySetInnerHTML={{
                  __html: t(translations.layout.categories[params.row.category.name]())
                }}
              />
            </a>
          );
        }
      },
      {
        field: 'date',
        flex: 1,
        headerName: t(translations.common.date()),
        renderCell: params => {
          return showDateField(params.row.category.name, params.row.date);
        }
      },
      {
        field: 'actions',
        flex: 1,
        headerName: t(translations.common.actions()),
        sortable: false,
        renderCell: params => {
          return (
            <Button size="small" disabled={loading} onClick={e => handleDelete(params.row.id)}>
              {t(translations.pages.watchlist.columns.delete())}
            </Button>
          );
        }
      }
    ];
  }, [handleDelete, loading, showDateField, t]);
};
